import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useState, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { Sidebar } from "@/components/layout/Sidebar";
import { AuthenticatedTopBar } from "@/components/layout/AuthenticatedTopBar";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import Dashboard from "./pages/Dashboard";
import Works from "./pages/Works";
import Tasks from "./pages/Tasks";
import Reports from "./pages/Reports";
import ObraIA from "./pages/ObraIA";
import ExportObra from "./pages/ExportObra";
import NotFound from "./pages/NotFound";
import Cadastro from "./pages/Cadastro";
import Login from "./pages/Login";
import Configuracoes from "./pages/Configuracoes";
import RDO from "./pages/RDO";
import PreenchendoRDO from "./pages/PreenchendoRDO";
import RDOPublic from "./pages/RDOPublic";
import RDOPrint from "./pages/RDOPrint";
import RDOFlagsDevPage from "./pages/dev/rdo-flags";
import CoverageDevPage from "./pages/dev/coverage";

const queryClient = new QueryClient();

const Protected = ({ children }: { children: JSX.Element }) => (
  <ProtectedRoute>{children}</ProtectedRoute>
);

const AppLayout = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isPrintRoute = location.pathname.includes("/print");
  const isPublicRoute =
    location.pathname === "/login" ||
    location.pathname.startsWith("/rdo-public") ||
    isPrintRoute;

  // Fecha o drawer mobile ao navegar
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  return (
    <>
      {!isPrintRoute && !isPublicRoute && (
        <Sidebar
          key="main-sidebar"
          mobileOpen={mobileMenuOpen}
          onMobileClose={() => setMobileMenuOpen(false)}
        />
      )}
      <div className={isPrintRoute || isPublicRoute ? "w-full" : "flex-1 flex flex-col min-w-0"}>
        {!isPrintRoute && !isPublicRoute && (
          <AuthenticatedTopBar onMenuClick={() => setMobileMenuOpen(true)} />
        )}
        <main className={isPrintRoute || isPublicRoute ? "w-full" : "flex-1 min-w-0"}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/rdo-public/:id" element={<RDOPublic />} />
          <Route path="/rdo/:obraId/:date/print" element={<RDOPrint />} />

          {/* Protected routes */}
          <Route path="/" element={<Protected><Dashboard /></Protected>} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/obras" element={<Protected><Works /></Protected>} />
          <Route path="/tarefas" element={<Protected><Tasks /></Protected>} />
          {/* Gestor de Tarefas 2 foi unificado no Gestor de Tarefas — mantém o
              redirect para não quebrar links salvos. */}
          <Route path="/tarefas2" element={<Navigate to="/tarefas" replace />} />
          <Route path="/relatorios" element={<Protected><Reports /></Protected>} />
          <Route path="/rdo" element={<Protected><RDO /></Protected>} />
          <Route path="/rdo/preencher" element={<Protected><PreenchendoRDO /></Protected>} />
          <Route path="/cadastro/*" element={<Protected><Cadastro /></Protected>} />
          <Route path="/obra-ia" element={<Protected><ObraIA /></Protected>} />
          <Route path="/export-obra" element={<Protected><ExportObra /></Protected>} />
          <Route path="/configuracoes" element={<Protected><Configuracoes /></Protected>} />

          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          {import.meta.env.DEV && (
            <Route path="/dev/rdo-flags" element={<RDOFlagsDevPage />} />
          )}
          {import.meta.env.DEV && (
            <Route path="/dev/coverage" element={<CoverageDevPage />} />
          )}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      </div>
    </>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <div className="flex min-h-screen w-full">
              <AppLayout />
            </div>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;

